import { useContext, useState, useEffect } from "react";
import { useAppStateContext } from "../../state/AppContextProvider";
import { useNavigate } from "react-router-dom";
import { useConfig } from "../../config/ConfigProvider";
import productImgDark from "../../assets/product4_dark.png";
import productImgLight from "../../assets/product4_light.png";
import { getUserPhoto } from "../../auth/getAccessToken";
import { AccessContext } from "../../state/AccessProvider";
import { Box, Button, useTheme, Card, CardContent, Typography } from "@mui/material";
import { HttpServiceContext } from "../../state/HttpProvider";
import { useOAuth } from "../../auth/OAuthProvider";

// Subscription plan types
type SubscriptionPlan = {
  id: string;
  name: string;
  priceId: string;
  productId: string;
  price: number;
  billingInterval: 'monthly' | 'yearly';
  tier: 'personal' | 'organizational-sharing' | 'organizational-advanced';
  description: string;
  features: string[];
  recommended?: boolean;
};

const PRODUCT_ID = 'prod_THMVFtg5m2VZUG';

const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  // Monthly Plans
  {
    id: 'personal-monthly',
    name: 'Personal Productivity',
    priceId: 'price_1SKnme3JJrvQVDu560DMJ9wK',
    productId: PRODUCT_ID,
    price: 25,
    billingInterval: 'monthly',
    tier: 'personal',
    description: 'Perfect for individual users',
    features: [
      'AI-powered insights',
      'Export conversations'
    ]
  },
  {
    id: 'organizational-sharing-monthly',
    name: 'Organizational (Sharing)',
    priceId: 'price_1SmLaP3JJrvQVDu5ht01H86I',
    productId: PRODUCT_ID,
    price: 50,
    billingInterval: 'monthly',
    tier: 'organizational-sharing',
    description: 'Team collaboration with shared resources',
    features: [
      'Everything in Personal',
      'Team collaboration',
      'Shared history',
    ],
    recommended: true
  },
  {
    id: 'organizational-advanced-monthly',
    name: 'Organizational (Advanced + Sharing)',
    priceId: 'price_1SmLav3JJrvQVDu5ZhxrN8dD',
    productId: PRODUCT_ID,
    price: 100,
    billingInterval: 'monthly',
    tier: 'organizational-advanced',
    description: 'Full enterprise features + team collaboration',
    features: [
      'Everything in Personal',
      'Team collaboration',
      'Shared history',
      'Advanced AI models',
    ]
  },
  // Yearly Plans
  {
    id: 'personal-yearly',
    name: 'Personal Productivity',
    priceId: 'price_1Sma9b3JJrvQVDu5WjBOUZuH',
    productId: PRODUCT_ID,
    price: 250,
    billingInterval: 'yearly',
    tier: 'personal',
    description: 'Perfect for individual users',
    features: [
      'AI-powered insights',
      'Export conversations',
      '💰 2 months free'
    ]
  },
  {
    id: 'organizational-sharing-yearly',
    name: 'Organizational (Sharing)',
    priceId: 'price_1SmaAC3JJrvQVDu5f5kurHPH',
    productId: PRODUCT_ID,
    price: 500,
    billingInterval: 'yearly',
    tier: 'organizational-sharing',
    description: 'Team collaboration with shared resources',
    features: [
      'Everything in Personal',
      'Team collaboration',
      'Shared history',
      '💰 2 months free'
    ],
    recommended: true
  },
  {
    id: 'organizational-advanced-yearly',
    name: 'Organizational (Advanced + Sharing)',
    priceId: 'price_1SmaBE3JJrvQVDu5GkuQb0LQ',
    productId: PRODUCT_ID,
    price: 1000,
    billingInterval: 'yearly',
    tier: 'organizational-advanced',
    description: 'Full enterprise features + team collaboration',
    features: [
      'Everything in Personal',
      'Team collaboration',
      'Shared history',
      'Advanced AI models',
      '💰 2 months free'
    ]
  }
];

interface SubscriptionFormProps {
  onSuccess: () => void;
}

const SubscriptionForm: React.FC<SubscriptionFormProps> = ({ 
  onSuccess
}) => {
  const { httpService } = useContext(HttpServiceContext);
  const accessProvider = useContext(AccessContext);
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    setLoadingPlanId(plan.id);
    setError(null);

    try {
      console.log('🔍 Selected Plan:', plan);
      console.log('🔍 Sending to backend:', {
        priceId: plan.priceId,
        productId: plan.productId
      });

      const response = await httpService.createCheckoutSession({
        email: accessProvider.decodedToken?.preferred_username || "",
        name: accessProvider.decodedToken?.name || accessProvider.decodedToken?.displayName || "",
        successUrl: `${window.location.origin}/#/subscription-success`,
        cancelUrl: `${window.location.origin}/#/subscription-cancel`,
        priceId: plan.priceId,
        productId: plan.productId
      });

      // Redirect to Stripe Checkout
      window.location.href = response.checkoutUrl;
    } catch (err: any) {
      setError(err.message || 'Failed to create checkout session');
      setLoadingPlanId(null);
    }
  };

  // Get unique tiers
  const tiers: ('personal' | 'organizational-sharing' | 'organizational-advanced')[] = ['personal', 'organizational-sharing', 'organizational-advanced'];

  return (
    <div style={{ marginTop: 12, width: "100%", textAlign: "center" }}>
      {/* Monthly/Yearly Toggle Buttons - Above all cards */}
      <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 3 }}>
        <Box sx={{ display: 'flex', border: '2px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
          <Button
            onClick={() => setBillingInterval('monthly')}
            sx={{
              padding: '10px 32px',
              borderRadius: 0,
              borderTopLeftRadius: '6px',
              borderBottomLeftRadius: '6px',
              backgroundColor: billingInterval === 'monthly' ? '#e50c20' : 'transparent',
              color: billingInterval === 'monthly' ? 'white' : 'text.primary',
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: billingInterval === 'monthly' ? '#c00a1a' : 'rgba(229, 12, 32, 0.1)',
              }
            }}
          >
            Monthly
          </Button>
          <Button
            onClick={() => setBillingInterval('yearly')}
            sx={{
              padding: '10px 32px',
              borderRadius: 0,
              borderTopRightRadius: '6px',
              borderBottomRightRadius: '6px',
              backgroundColor: billingInterval === 'yearly' ? '#e50c20' : 'transparent',
              color: billingInterval === 'yearly' ? 'white' : 'text.primary',
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: billingInterval === 'yearly' ? '#c00a1a' : 'rgba(229, 12, 32, 0.1)',
              }
            }}
          >
            Yearly
          </Button>
        </Box>
      </Box>

      {/* Plan cards - 3 CARDS */}
      <Box sx={{ display: 'flex', gap: 3, marginBottom: 3, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-start' }}>
        {tiers.map((tier) => {
          const plan = SUBSCRIPTION_PLANS.find(p => p.tier === tier && p.billingInterval === billingInterval)!;
          
          return (
            <Card
              key={tier}
              sx={{
                flex: '1 1 300px',
                maxWidth: 380,
                minWidth: 300,
                height: 375,
                border: '2px solid #e0e0e0',
                borderRadius: '16px',
                transition: 'all 0.2s ease',
                paddingTop: '5px',
                backgroundColor: 'background.paper',
                position: 'relative',
                '&:hover': {
                  borderColor: '#e50c20',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(229, 12, 32, 0.15)',
                  '& .subscribe-button': {
                    borderColor: '#e50c20',
                    color: '#e50c20',
                    backgroundColor: 'rgba(229, 12, 32, 0.05)',
                  }
                }
              }}
            >
              {plan.recommended && (
                <Box
                  sx={{
                    display: 'none',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#e50c20',
                    color: 'white',
                    padding: '4px 16px',
                    marginTop: '10px',
                    borderRadius: '12px',
                    fontSize: 12,
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(229, 12, 32, 0.3)'
                  }}
                >
                  MOST POPULAR
                </Box>
              )}
              <CardContent sx={{ padding: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, marginBottom: 1, color: 'text.primary', fontSize: 18, minHeight: 48 }}>
                  {plan.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', marginBottom: 2, fontSize: 13 }}>
                  {plan.description}
                </Typography>
                
                <Box sx={{ marginBottom: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#e50c20', display: 'inline' }}>
                    ${plan.price}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', display: 'inline' }}>
                    /{plan.billingInterval === 'monthly' ? 'mo' : 'yr'}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'left', flexGrow: 1, marginBottom: 2 }}>
                  {plan.features.map((feature, idx) => (
                    <Typography key={idx} variant="body2" sx={{ color: 'text.secondary', marginBottom: 0.5, fontSize: 13 }}>
                      {feature.startsWith('💰') ? feature : `✓ ${feature}`}
                    </Typography>
                  ))}
                </Box>
                <Button
                  onClick={() => handleSubscribe(plan)}
                  disabled={loadingPlanId === plan.id}
                  variant="outlined"
                  className="subscribe-button"
                  sx={{
                    width: "100%",
                    textTransform: "none",
                    borderColor: "text.secondary",
                    color: "text.primary",
                    padding: "12px 16px",
                    fontSize: 15,
                    fontWeight: 600,
                    borderRadius: "8px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "text.primary",
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                    },
                    "&:disabled": {
                      borderColor: "#ccc",
                      color: "#ccc",
                    }
                  }}
                >
                  {loadingPlanId === plan.id ? 'Processing...' : 'Subscribe'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Box sx={{ 
        marginBottom: 0, 
        padding: 0, 
        backgroundColor: "rgba(0, 0, 0, 0.05)",
        borderRadius: "8px",
        fontSize: 14,
        color: "text.secondary",
        maxWidth: 1000,
        marginLeft: 'auto',
        marginRight: 'auto',
        textAlign: 'center'
      }}>
        You'll be redirected to Stripe's secure checkout to complete your subscription.
      </Box>
      
      {error && (
        <div style={{ 
          color: "#d32f2f", 
          marginBottom: 16, 
          fontSize: "14px",
          textAlign: "center"
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

const RedirectLandingPageV3: React.FC = () => {
  const {
    isApproved,
    refreshApprovalStatus,
    subscriptionVerified,
    refreshSubscriptionStatus,
  } = useAppStateContext();
  const config = useConfig();
  const { refreshUser } = useOAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [showPending, setShowPending] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const accessProvider = useContext(AccessContext);
  const productImg = theme.palette.mode === 'dark' ? productImgDark : productImgLight;

  // Logout handler
  const handleLogout = async () => {
    await accessProvider.handleLogout();
  };

  // Redirect if approved
  useEffect(() => {
    if (isApproved) {
      navigate("/");
    }
  }, [isApproved, navigate]);

  // Show pending after 5 seconds if not approved
  useEffect(() => {
    if (isApproved !== true) {
      const timer = setTimeout(() => {
        setShowPending(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
    setShowPending(false);
    return undefined;
  }, [isApproved]);

  useEffect(() => {
    if (showPending) {
      localStorage.setItem("userSignedUp", "true");
    }
  }, [showPending]);

  // Show subscription after 1 second (just for aesthetics)
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSubscription(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Fetch profile photo
  useEffect(() => {
    const fetchProfilePhoto = async () => {
      try {
        if (accessProvider.accessToken) {
          const photo = await getUserPhoto(accessProvider.accessToken);
          setProfilePhoto(photo);
        }
      } catch (error) {
        console.error("Error fetching profile photo:", error);
      }
    };
    fetchProfilePhoto();
  }, [accessProvider.accessToken]);

  const handleCheckAccess = async () => {
    try {
      await refreshApprovalStatus();
      await refreshSubscriptionStatus();
      
      if (isApproved) {
        navigate("/");
      }
    } catch (error) {
      console.error("Error checking access:", error);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        padding: "16px",
        bgcolor: "background.default",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 1200,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Box sx={{ fontSize: 24, fontWeight: 700, color: 'text.primary' }}>
          Negotiator Power Up
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {profilePhoto && (
            <img
              src={profilePhoto}
              alt="Profile"
              style={{ width: 40, height: 40, borderRadius: "50%" }}
            />
          )}
          {showPending && !isApproved && (
            <Button
              onClick={handleCheckAccess}
              variant="outlined"
              sx={{
                textTransform: "none",
                borderColor: "text.secondary",
                color: "text.primary",
                "&:hover": {
                  borderColor: "text.primary",
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                },
              }}
            >
              Check Access
            </Button>
          )}
          <Button
            onClick={handleLogout}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderColor: "#e50c20",
              color: "#e50c20",
              "&:hover": {
                borderColor: "#c00a1a",
                backgroundColor: "rgba(229, 12, 32, 0.05)",
              },
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* Main content - Full width subscription form */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 1400,
          position: "relative",
        }}
      >
        {/* Overlay */}
        {showOverlay && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(60,60,60,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: 16,
            }}
          >
            <Box
              component="span"
              sx={{
                color: "text.secondary",
                fontSize: 24,
                fontWeight: 700,
                textAlign: "center",
                letterSpacing: "0.03em",
                lineHeight: 1.4,
                textShadow: "0 2px 12px rgba(0,0,0,0.18)",
                padding: "0 16px",
              }}
            >
              Subscriptions are part of beta release. Please check back later.
            </Box>
          </div>
        )}

        {/* Hero section */}
        <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, marginBottom: 1, color: 'text.primary' }}>
            Choose Your Plan
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', marginBottom: 2 }}>
            Maximize your negotiation potential with AI-powered insights
          </Typography>
          <img src={productImg} alt="Product" style={{ display: 'none' }} />
        </Box>

        {showSubscription && (
          <SubscriptionForm
            onSuccess={async () => {
              await refreshApprovalStatus();
            }}
          />
        )}

        {/* Footer */}
        <Box sx={{ marginTop: 3, textAlign: 'center', fontSize: 12, color: "#888" }}>
          Powered by <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>stripe</Box> &nbsp; | &nbsp; 
          <a href="#" style={{ color: '#888', textDecoration: 'none' }}>Terms</a> &nbsp; 
          <a href="#" style={{ color: '#888', textDecoration: 'none' }}>Privacy</a>
        </Box>
      </Box>
    </Box>
  );
};

export default RedirectLandingPageV3;
